import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { midnight } from './midnightStyles';

export interface ContestWizardFooterProps {
    onBack?: () => void;
    backLabel?: string;
    showBackIcon?: boolean;
    onSaveDraft: () => void;
    onResetForm?: () => void;
    onContinue: () => void;
    continueLabel: string;
    disableContinue?: boolean;
    draftSavedHint?: string | null;
}

export function ContestWizardFooter({
    onBack,
    backLabel = 'Back',
    showBackIcon = true,
    onSaveDraft,
    onResetForm,
    onContinue,
    continueLabel,
    disableContinue,
    draftSavedHint,
}: ContestWizardFooterProps) {
    return (
        <Box
            component="footer"
            sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                mt: 4,
                mx: { xs: -2, sm: 0 },
                px: { xs: 2, sm: 3 },
                py: 3,
                bgcolor: 'rgba(26, 25, 25, 0.85)',
                backdropFilter: 'blur(20px)',
                borderTop: `1px solid ${midnight.surfaceHighest}`,
                borderRadius: { xs: 0, sm: '16px 16px 0 0' },
            }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    maxWidth: 900,
                    mx: 'auto',
                }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Button
                            onClick={onSaveDraft}
                            sx={{
                                color: midnight.onSurfaceVariant,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.2em',
                                textTransform: 'uppercase',
                                '&:hover': { color: midnight.onSurface, bgcolor: 'transparent' },
                            }}>
                            Save Draft
                        </Button>
                        {onResetForm && (
                            <Button
                                onClick={onResetForm}
                                sx={{
                                    color: midnight.onSurfaceVariant,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: '0.2em',
                                    textTransform: 'uppercase',
                                    '&:hover': { color: midnight.onSurface, bgcolor: 'transparent' },
                                }}>
                                Reset form
                            </Button>
                        )}
                    </Box>
                    {draftSavedHint && (
                        <Typography sx={{ fontSize: 10, color: midnight.secondary, letterSpacing: '0.08em' }}>
                            {draftSavedHint}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    {onBack && (
                        <Button
                            onClick={onBack}
                            startIcon={showBackIcon ? <ArrowBackIosNewIcon sx={{ fontSize: 12 }} /> : undefined}
                            sx={{
                                px: 3,
                                py: 1.5,
                                borderRadius: 999,
                                color: midnight.onSurfaceVariant,
                                fontWeight: 700,
                                fontSize: 11,
                                letterSpacing: '0.15em',
                                textTransform: 'uppercase',
                                '&:hover': { color: midnight.onSurface, bgcolor: midnight.surfaceHighest },
                            }}>
                            {backLabel}
                        </Button>
                    )}
                    <Button
                        onClick={onContinue}
                        disabled={disableContinue}
                        sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: 999,
                            fontWeight: 800,
                            fontSize: 12,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            color: '#392700',
                            background: `linear-gradient(90deg, ${midnight.primary}, #feb700)`,
                            boxShadow: '0 10px 30px rgba(255,201,101,0.2)',
                            '&:hover': {
                                boxShadow: '0 15px 40px rgba(255,201,101,0.3)',
                            },
                            '&:disabled': { opacity: 0.45 },
                        }}>
                        {continueLabel}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
